import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import { CohortRetentionData } from '../../../types/admin';

interface CohortRetentionTableProps {
  data: CohortRetentionData[];
}

/**
 * CohortRetentionTable - Heatmap table for cohort retention
 *
 * Displays cohort retention with color-coded cells
 * - Green gradient for retention percentages
 * - Darker green = higher retention
 * - Lighter green = lower retention
 * - Tooltips show exact percentages
 */
export default function CohortRetentionTable({ data }: CohortRetentionTableProps) {
  // Function to get color based on retention percentage
  const getRetentionColor = (percentage: number): string => {
    if (percentage >= 80) return '#1b5e20'; // Dark green
    if (percentage >= 70) return '#2e7d32';
    if (percentage >= 60) return '#388e3c';
    if (percentage >= 50) return '#43a047';
    if (percentage >= 40) return '#66bb6a';
    if (percentage >= 30) return '#81c784';
    return '#a5d6a7'; // Light green
  };

  const retentionPeriods = [
    { key: 'day_0', label: 'Day 0' },
    { key: 'day_7', label: 'Day 7' },
    { key: 'day_14', label: 'Day 14' },
    { key: 'day_30', label: 'Day 30' },
    { key: 'day_60', label: 'Day 60' },
    { key: 'day_90', label: 'Day 90' },
  ];

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Cohort</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Size</strong>
            </TableCell>
            {retentionPeriods.map((period) => (
              <TableCell key={period.key} align="center">
                <strong>{period.label}</strong>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((cohort) => (
            <TableRow key={cohort.cohort_date} hover>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {cohort.cohort_date}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body2">{cohort.cohort_size.toLocaleString()}</Typography>
              </TableCell>
              {retentionPeriods.map((period) => {
                const retentionValue =
                  cohort.retention[period.key as keyof typeof cohort.retention];
                return (
                  <Tooltip
                    key={period.key}
                    title={`${retentionValue}% retained at ${period.label}`}
                    arrow
                  >
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: getRetentionColor(retentionValue),
                        color: retentionValue >= 50 ? 'white' : 'black',
                        fontWeight: 'medium',
                        cursor: 'pointer',
                      }}
                    >
                      {retentionValue}%
                    </TableCell>
                  </Tooltip>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
        <Typography variant="caption" color="text.secondary">
          Retention rates are color-coded: darker green indicates higher retention, lighter green
          indicates lower retention.
        </Typography>
      </Box>
    </TableContainer>
  );
}
